import { createFeature, createReducer, on } from '@ngrx/store';
import { MetadataActions, TemplateType } from './metadata.actions';
import { LoadingEntity } from '../../shared/models/loading-state';
import { RoleModel } from '../../../libs/role';
import { GroupTypeModel } from '../../../libs/system';
import { AccountModel, ObjectRelation, ObjectRelationSetting, VatCodeModel } from '@rillion/api/models/master-data.models';

export interface MetaRole extends RoleModel {
  /**
   * The role and the usernames concatenated and lower cased to ease searching and increase performance
   */
  searchable: string;
  /**
   * Usernames joined and ready to display
   */
  joinedUsernames: string;
}

export interface MetadataState {
  metaRoles: LoadingEntity<MetaRole[]>;
  groupTypes: LoadingEntity<Record<TemplateType, GroupTypeModel[]>>;
  accounts: LoadingEntity<AccountModel[]>;
  vatCodes: LoadingEntity<VatCodeModel[]>;
  objectRelations: LoadingEntity<ObjectRelation[]>;
  objectRelationSettings: LoadingEntity<ObjectRelationSetting[]>;
}

export const metadataInitialState: MetadataState = {
  metaRoles: LoadingEntity.initial(),
  groupTypes: LoadingEntity.initial(),
  accounts: LoadingEntity.initial(),
  vatCodes: LoadingEntity.initial(),
  objectRelations: LoadingEntity.initial(),
  objectRelationSettings: LoadingEntity.initial(),
};

export const metadataFeature = createFeature({
  name: 'metadata',
  reducer: createReducer(
    metadataInitialState,

    // Meta roles

    on(
      MetadataActions.getRoles,
      (state): MetadataState => ({
        ...state,
        metaRoles: LoadingEntity.loading(),
      }),
    ),
    on(
      MetadataActions.getRolesSuccess,
      (state, { roles }): MetadataState => ({
        ...state,
        metaRoles: LoadingEntity.loaded(
          roles.map((role) => ({
            ...role,
            searchable: `${role.role} ${role.roleUserNames.join(' ')}`.toLowerCase(),
            joinedUsernames: role.roleUserNames.join(', '),
          })),
        ),
      }),
    ),
    on(
      MetadataActions.getRolesFailure,
      (state, { error }): MetadataState => ({
        ...state,
        metaRoles: LoadingEntity.failed(error),
      }),
    ),

    // Group types

    on(
      MetadataActions.getGroupTypes,
      (state): MetadataState => ({
        ...state,
        groupTypes: LoadingEntity.loading(true),
      }),
    ),
    on(
      MetadataActions.getGroupTypesSuccess,
      (state, { template, groupTypes }): MetadataState => ({
        ...state,
        groupTypes: LoadingEntity.loaded({
          ...state.groupTypes.entity,
          [template]: groupTypes,
        }),
      }),
    ),
    on(
      MetadataActions.getGroupTypesFailure,
      (state, { error }): MetadataState => ({
        ...state,
        groupTypes: LoadingEntity.failed(error),
      }),
    ),

    // Accounts

    on(
      MetadataActions.getAccounts,
      (state): MetadataState => ({
        ...state,
        accounts: LoadingEntity.loading(),
      }),
    ),
    on(
      MetadataActions.getAccountsSuccess,
      (state, { accounts }): MetadataState => ({
        ...state,
        accounts: LoadingEntity.loaded(accounts),
      }),
    ),
    on(
      MetadataActions.getAccountsFailure,
      (state, { error }): MetadataState => ({
        ...state,
        accounts: LoadingEntity.failed(error),
      }),
    ),

    // Vat codes

    on(
      MetadataActions.getVatCodes,
      (state): MetadataState => ({
        ...state,
        accounts: LoadingEntity.loading(),
      }),
    ),
    on(
      MetadataActions.getVatCodesSuccess,
      (state, { vatCodes }): MetadataState => ({
        ...state,
        vatCodes: LoadingEntity.loaded(vatCodes),
      }),
    ),
    on(
      MetadataActions.getVatCodesFailure,
      (state, { error }): MetadataState => ({
        ...state,
        vatCodes: LoadingEntity.failed(error),
      }),
    ),

    // Object relations
    on(
      MetadataActions.getObjectRelations,
      (state): MetadataState => ({
        ...state,
        objectRelations: LoadingEntity.loading(),
      }),
    ),
    on(
      MetadataActions.getObjectRelationsSuccess,
      (state, { objectRelations }): MetadataState => ({
        ...state,
        objectRelations: LoadingEntity.loaded(objectRelations),
      }),
    ),
    on(
      MetadataActions.getObjectRelationsFailure,
      (state, { error }): MetadataState => ({
        ...state,
        objectRelations: LoadingEntity.failed(error),
      }),
    ),

    // Object relation settings
    on(
      MetadataActions.getObjectRelationSettings,
      (state): MetadataState => ({
        ...state,
        objectRelationSettings: LoadingEntity.loading(),
      }),
    ),
    on(
      MetadataActions.getObjectRelationSettingsSuccess,
      (state, { objectRelationSettings }): MetadataState => ({
        ...state,
        objectRelationSettings: LoadingEntity.loaded(objectRelationSettings),
      }),
    ),
    on(
      MetadataActions.getObjectRelationSettingsFailure,
      (state, { error }): MetadataState => ({
        ...state,
        objectRelationSettings: LoadingEntity.failed(error),
      }),
    ),
  ),
});
